import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import {
  useAddChecklistItem,
  useCreateQualification,
  useProjectDashboard,
} from '../../hooks/useOps';
import { humanizeStatus, statusColor } from '../../lib/format';
import { qualificationPct } from './quality';

const QUAL_TYPES = [
  { value: 'iq', label: 'IQ' },
  { value: 'oq', label: 'OQ' },
  { value: 'pq', label: 'PQ' },
];

/** IQ/OQ/PQ checklists with a completion rollup. Detail view stays its own page. */
export function QualityTab({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { data } = useProjectDashboard(projectId);
  const createQual = useCreateQualification(projectId);
  const addQualItem = useAddChecklistItem(projectId);

  const [addingQualItem, setAddingQualItem] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!data) return null;
  const { qualifications } = data;
  const existingQualTypes = new Set(qualifications.map((q) => q.type));

  const handleAddQualItem = async (checklistId: string) => {
    if (!description) return;
    try {
      await addQualItem.mutateAsync({ checklistId, description });
      setAddingQualItem(null);
      setDescription('');
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Failed to add item');
    }
  };

  return (
    <Stack gap={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
          Qualifications
        </Typography>

        <Stack direction="row" gap={1} mb={qualifications.length ? 2 : 1}>
          {QUAL_TYPES.map((qt) => (
            <Button
              key={qt.value}
              size="small"
              variant="outlined"
              disabled={existingQualTypes.has(qt.value as never) || createQual.isPending}
              onClick={() => createQual.mutate({ projectId, type: qt.value })}
            >
              + {qt.label}
            </Button>
          ))}
        </Stack>

        {qualifications.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            No checklists yet.
          </Typography>
        )}

        {qualifications.map((ql) => {
          const pct = qualificationPct(ql);
          return (
            <Accordion key={ql.id} disableGutters variant="outlined" sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" gap={1.5} alignItems="center" sx={{ width: '100%' }} pr={1}>
                  <Typography variant="body2" fontWeight={600}>
                    {ql.type.toUpperCase()}
                  </Typography>
                  <Chip
                    size="small"
                    label={humanizeStatus(ql.status)}
                    color={statusColor(ql.status)}
                    variant="outlined"
                  />
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    color={pct === 100 ? 'success' : 'primary'}
                    sx={{ flex: 1, height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 64 }}>
                    {pct}% · {(ql.items ?? []).length} items
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack gap={0.5} mb={1}>
                  {(ql.items ?? []).length === 0 && (
                    <Typography color="text.secondary" variant="body2">
                      No items yet.
                    </Typography>
                  )}
                  {[...(ql.items ?? [])]
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((item) => (
                      <Stack key={item.id} direction="row" gap={1} alignItems="flex-start">
                        <Typography variant="body2" sx={{ minWidth: 24, color: 'text.secondary' }}>
                          {item.sequence}.
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {item.description}
                        </Typography>
                        <Chip size="small" label={item.result} variant="outlined" />
                      </Stack>
                    ))}
                </Stack>

                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  {addingQualItem === ql.id ? (
                    <>
                      <TextField
                        size="small"
                        label="Item description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        sx={{ width: 320 }}
                        autoFocus
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAddQualItem(ql.id)}
                        disabled={addQualItem.isPending || !description}
                      >
                        Add
                      </Button>
                      <Button size="small" onClick={() => setAddingQualItem(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setAddingQualItem(ql.id);
                        setDescription('');
                      }}
                    >
                      Add item
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(`/projects/${projectId}/qualifications/${ql.id}`)}
                  >
                    Open checklist
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Paper>

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
